import React, { useEffect, useRef, useState, memo } from "react";
import { getFadeGain } from "./store";

// ===== Waveform Cache =====
// Cache waveform data per audio URL to avoid re-computing
const waveformCache = new Map(); // url -> Float32Array of samples
const waveformFailedUrls = new Set(); // urls where decode failed
const waveformInFlight = new Map(); // url -> Promise (dedup concurrent requests)

function processAudioBuffer(audioBuffer) {
  const rawData = audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;
  const duration = audioBuffer.duration;
  const totalRawSamples = rawData.length;

  const samplesCount = Math.max(100, Math.min(5000, Math.round(duration * 50)));
  const samples = new Float32Array(samplesCount);

  for (let i = 0; i < samplesCount; i++) {
    const start = Math.floor((i / samplesCount) * totalRawSamples);
    const end = Math.min(Math.floor(((i + 1) / samplesCount) * totalRawSamples), totalRawSamples);
    const count = end - start;
    if (count <= 0) { samples[i] = 0; continue; }

    let sumSq = 0;
    let peak = 0;
    for (let j = start; j < end; j++) {
      const v = rawData[j];
      sumSq += v * v;
      const abs = Math.abs(v);
      if (abs > peak) peak = abs;
    }
    const rms = Math.sqrt(sumSq / count);
    samples[i] = rms * 0.7 + peak * 0.3;
  }

  const sorted = Float32Array.from(samples).sort();
  const p95 = sorted[Math.floor(sorted.length * 0.95)] || 1;
  const normFactor = p95 > 0 ? p95 : 1;
  for (let i = 0; i < samples.length; i++) {
    samples[i] = Math.min(1, samples[i] / normFactor);
  }

  return { samples, duration, sampleRate };
}

async function extractWaveformData(url) {
  if (waveformCache.has(url)) {
    return waveformCache.get(url);
  }
  if (waveformFailedUrls.has(url)) {
    return null;
  }
  if (waveformInFlight.has(url)) {
    return waveformInFlight.get(url);
  }

  const work = (async () => {
    let audioContext = null;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const arrayBuffer = await response.arrayBuffer();

      // Bail out for very large files (>50 MB) to prevent OOM crashes
      if (arrayBuffer.byteLength > 50 * 1024 * 1024) {
        console.warn("Skipping waveform for large file:", (arrayBuffer.byteLength / 1024 / 1024).toFixed(1), "MB");
        waveformFailedUrls.add(url);
        return null;
      }

      audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Use OfflineAudioContext for decoding so we don't block the live context
      // and wrap in a timeout to avoid hanging on problematic files.
      const audioBuffer = await Promise.race([
        audioContext.decodeAudioData(arrayBuffer),
        new Promise((_, reject) => setTimeout(() => reject(new Error("decodeAudioData timeout")), 15000)),
      ]);

      const result = processAudioBuffer(audioBuffer);
      waveformCache.set(url, result);
      return result;
    } catch (error) {
      console.error("Failed to extract waveform:", error);
      waveformFailedUrls.add(url);
      return null;
    } finally {
      waveformInFlight.delete(url);
      if (audioContext) {
        try { audioContext.close(); } catch {}
      }
    }
  })();

  waveformInFlight.set(url, work);
  return work;
}

export const WaveformCanvas = memo(function WaveformCanvas({ url, width, height, inPoint = 0, duration, assetDuration, color = "rgba(16, 185, 129, 0.6)", gain = 1, fadeIn = 0, fadeOut = 0, clipStart = 0, onDecodeFailed }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const [waveformData, setWaveformData] = useState(null);

  useEffect(() => {
    if (!url) return;
    
    let cancelled = false;
    extractWaveformData(url).then((data) => {
      if (cancelled) return;
      if (data) {
        setWaveformData(data);
      } else {
        onDecodeFailed?.();
      }
    });
    
    return () => { cancelled = true; };
  }, [url]);

  useEffect(() => {
    // Cancel any pending animation frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    // Use requestAnimationFrame for smooth updates during slider dragging
    rafRef.current = requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas || !waveformData) return;
      
      const ctx = canvas.getContext("2d");
      const dpr = window.devicePixelRatio || 1;
      
      // Set canvas size with DPR for crisp rendering
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
      
      // Clear canvas
      ctx.clearRect(0, 0, width, height);
      
      const { samples, duration: decodedDuration } = waveformData;
      const totalDuration = decodedDuration;
      const numSamples = samples.length;
      
      if (numSamples === 0 || totalDuration <= 0) {
        rafRef.current = null;
        return;
      }
      
      // Draw one bar per pixel column. For each pixel we compute the exact
      // time in the source audio file, convert that to a fractional index
      // into the samples array, and linearly interpolate.  This avoids
      // the slice + re-index approach which introduced rounding drift.
      ctx.fillStyle = color;
      
      const clipForFade = {
        start: clipStart,
        duration: duration,
        fadeIn: fadeIn,
        fadeOut: fadeOut,
      };
      
      const numBars = Math.max(1, Math.floor(width));
      const barWidth = width / numBars;
      
      for (let i = 0; i < numBars; i++) {
        const t = (i + 0.5) / numBars;
        
        const audioTime = inPoint + t * duration;
        
        const samplePos = (audioTime / totalDuration) * (numSamples - 1);
        const idx0 = Math.max(0, Math.min(numSamples - 1, Math.floor(samplePos)));
        const idx1 = Math.min(idx0 + 1, numSamples - 1);
        const frac = samplePos - idx0;
        const sample = samples[idx0] * (1 - frac) + samples[idx1] * frac;
        
        const timeInClip = t * duration;
        const timelineTime = clipStart + timeInClip;
        const fadeMultiplier = getFadeGain(clipForFade, timelineTime);
        
        const adjustedSample = sample * gain * fadeMultiplier;
        const clampedSample = Math.max(0, Math.min(1, adjustedSample));
        
        const barHeight = clampedSample * (height * 0.9);
        const x = i * barWidth;
        const y = height - barHeight;
        
        ctx.fillRect(x, y, Math.max(1, barWidth - 0.5), barHeight);
      }
      
      rafRef.current = null;
    });
    
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [waveformData, width, height, inPoint, duration, color, gain, fadeIn, fadeOut, clipStart]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: width,
        height: height,
        pointerEvents: "none",
      }}
    />
  );
});

// Custom comparison for GainFadeCanvas to ensure it updates when gain/fade values change
function areGainFadePropsEqual(prevProps, nextProps) {
  // Compare the specific clip properties that affect the visualization
  if (prevProps.clip.gain !== nextProps.clip.gain) return false;
  if (prevProps.clip.fadeIn !== nextProps.clip.fadeIn) return false;
  if (prevProps.clip.fadeOut !== nextProps.clip.fadeOut) return false;
  if (prevProps.clip.duration !== nextProps.clip.duration) return false;
  if (prevProps.width !== nextProps.width) return false;
  if (prevProps.height !== nextProps.height) return false;
  if (prevProps.color !== nextProps.color) return false;
  return true;
}

export const GainFadeCanvas = memo(function GainFadeCanvas({ clip, width, height, color = "rgba(255, 255, 255, 0.5)" }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  
  // Extract values to ensure effect triggers on changes
  const gain = clip.gain ?? 1;
  const fadeIn = clip.fadeIn ?? 0;
  const fadeOut = clip.fadeOut ?? 0;
  const duration = clip.duration;
  
  useEffect(() => {
    // Cancel any pending animation frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    // Use requestAnimationFrame for smooth updates during slider dragging
    rafRef.current = requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext("2d");
      const dpr = window.devicePixelRatio || 1;
      
      // Set canvas size with DPR for crisp rendering
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
      
      // Clear canvas
      ctx.clearRect(0, 0, width, height);
    
    // Padding at top and bottom (few pixels)
    const padding = 4;
    const drawHeight = height - (padding * 2);
    const drawBottom = height - padding;
    
    // Gain range: 0 to 5 (max gain from slider)
    const maxGain = 5;
    
    // Calculate the Y position for a given gain value using logarithmic scale
    // Gain 1 = center, Gain 0 = bottom, Gain 5 = top
    const gainToY = (g) => {
      const clampedGain = Math.max(0, Math.min(maxGain, g));
      
      // Use logarithmic scale with offset to center gain 1
      // Offset = 1/3 ensures log(1 + offset) is exactly at the midpoint
      // between log(0 + offset) and log(5 + offset)
      const offset = 1 / 3;
      const minLog = Math.log(0 + offset);
      const centerLog = Math.log(1 + offset);
      const maxLog = Math.log(maxGain + offset);
      
      let normalized;
      if (clampedGain <= 1) {
        // Map gain 0-1 to normalized 0-0.5 (bottom half)
        const logValue = Math.log(clampedGain + offset);
        normalized = 0.5 * (logValue - minLog) / (centerLog - minLog);
      } else {
        // Map gain 1-5 to normalized 0.5-1 (top half)
        const logValue = Math.log(clampedGain + offset);
        normalized = 0.5 + 0.5 * (logValue - centerLog) / (maxLog - centerLog);
      }
      
      return drawBottom - (normalized * drawHeight);
    };
    
    // Gain level Y position (horizontal line position)
    const gainY = gainToY(gain);
    
    // Ensure fade durations don't exceed clip duration
    const maxFadeIn = Math.min(fadeIn, duration);
    const maxFadeOut = Math.min(fadeOut, duration);
    
    // Handle case where fadeIn + fadeOut > duration
    // If fades overlap, we need to blend them
    const fadeOutStartTime = duration - maxFadeOut;
    const fadeInEndTime = maxFadeIn;
    
    // If fade out starts before fade in ends, we need to handle the overlap
    const hasOverlap = fadeOutStartTime < fadeInEndTime && maxFadeIn > 0 && maxFadeOut > 0;
    
    // Convert time to X position
    const timeToX = (t) => (t / duration) * width;
    
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    
    ctx.beginPath();
    
    // Helper function to draw smooth fade curve using multiple points
    // Uses ease-in-out cubic for smooth audio fade
    const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    
    // Start at the left edge
    let currentX = 0;
    let currentY = drawBottom; // Start at bottom (min gain)
    
    if (hasOverlap) {
      // Handle overlapping fades - blend them together
      // The peak gain will be reduced in the overlap region
      const overlapStartX = timeToX(fadeOutStartTime);
      const overlapEndX = timeToX(fadeInEndTime);
      const overlapWidth = overlapEndX - overlapStartX;
      
      // Draw fade in up to overlap start
      if (fadeOutStartTime > 0) {
        const fadeInPartialX = timeToX(fadeOutStartTime);
        // At fadeOutStartTime, we're still in fade in, so calculate the gain
        const timeAtOverlapStart = fadeOutStartTime;
        const fadeInMultiplier = maxFadeIn > 0 ? Math.min(1, timeAtOverlapStart / maxFadeIn) : 1;
        const fadeInPartialY = gainToY(gain * fadeInMultiplier);
        
        ctx.moveTo(currentX, currentY);
        
        const segments = Math.max(10, Math.floor(fadeInPartialX / 2));
        for (let i = 1; i <= segments; i++) {
          const t = i / segments;
          const easedT = easeInOutCubic(t);
          const x = currentX + (fadeInPartialX - currentX) * t;
          const y = currentY + (fadeInPartialY - currentY) * easedT;
          ctx.lineTo(x, y);
        }
        
        currentX = fadeInPartialX;
        currentY = fadeInPartialY;
      } else {
        // Fade out starts immediately, so we start at bottom
        ctx.moveTo(currentX, currentY);
      }
      
      // Draw overlap region - blend between fade in and fade out
      // In the overlap, we need to calculate the combined gain
      const overlapSegments = Math.max(10, Math.floor(overlapWidth / 2));
      for (let i = 0; i <= overlapSegments; i++) {
        const t = i / overlapSegments;
        const x = overlapStartX + overlapWidth * t;
        
        // Calculate time in clip
        const timeInClip = (x / width) * duration;
        
        // Calculate fade in multiplier (0 to 1)
        const fadeInMultiplier = maxFadeIn > 0 ? Math.min(1, timeInClip / maxFadeIn) : 1;
        // Calculate fade out multiplier (1 to 0, where 1 = not faded, 0 = fully faded)
        const timeFromEnd = duration - timeInClip;
        const fadeOutMultiplier = maxFadeOut > 0 ? Math.min(1, timeFromEnd / maxFadeOut) : 1;
        
        // Combined gain: multiply fade in and fade out multipliers
        // Both are 0-1, so multiplying gives us the combined effect
        const combinedMultiplier = fadeInMultiplier * fadeOutMultiplier;
        const combinedGain = gain * combinedMultiplier;
        const y = gainToY(combinedGain);
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      
      // After overlap, continue with fade out
      // Calculate the remaining fade out portion
      const remainingFadeOutTime = duration - fadeInEndTime;
      if (remainingFadeOutTime > 0) {
        const fadeOutEndX = width;
        const fadeOutEndY = drawBottom;
        
        // Calculate starting point (end of overlap)
        const timeAtOverlapEnd = fadeInEndTime;
        const timeFromEndAtOverlapEnd = duration - timeAtOverlapEnd;
        const fadeOutMultiplierAtEnd = maxFadeOut > 0 ? Math.min(1, timeFromEndAtOverlapEnd / maxFadeOut) : 1;
        const startY = gainToY(gain * fadeOutMultiplierAtEnd);
        const startX = overlapEndX;
        
        // Draw fade out curve from overlap end to clip end
        const fadeOutSegments = Math.max(10, Math.floor((fadeOutEndX - startX) / 2));
        for (let i = 1; i <= fadeOutSegments; i++) {
          const t = i / fadeOutSegments;
          const x = startX + (fadeOutEndX - startX) * t;
          
          // Calculate time in clip
          const timeInClip = (x / width) * duration;
          const timeFromEnd = duration - timeInClip;
          const fadeOutMultiplier = maxFadeOut > 0 ? Math.min(1, timeFromEnd / maxFadeOut) : 1;
          const y = gainToY(gain * fadeOutMultiplier);
          
          ctx.lineTo(x, y);
        }
      }
    } else {
      // No overlap - draw fades normally
      // Fade in curve
      if (maxFadeIn > 0) {
        const fadeInEndX = timeToX(maxFadeIn);
        const fadeInEndY = gainY; // End at gain level
        
        // Draw smooth curve from bottom to gain level using multiple segments
        ctx.moveTo(currentX, currentY);
        
        const segments = Math.max(10, Math.floor(fadeInEndX / 2)); // Enough segments for smooth curve
        for (let i = 1; i <= segments; i++) {
          const t = i / segments;
          const easedT = easeInOutCubic(t);
          const x = currentX + (fadeInEndX - currentX) * t;
          const y = currentY + (fadeInEndY - currentY) * easedT;
          ctx.lineTo(x, y);
        }
        
        currentX = fadeInEndX;
        currentY = fadeInEndY;
      } else {
        // No fade in - start at gain level
        ctx.moveTo(currentX, gainY);
        currentX = 0;
        currentY = gainY;
      }
      
      // Horizontal line at gain level (from fade in end to fade out start)
      const fadeOutStartX = maxFadeOut > 0 ? timeToX(duration - maxFadeOut) : width;
      ctx.lineTo(fadeOutStartX, gainY);
      
      // Fade out curve
      if (maxFadeOut > 0) {
        const fadeOutEndX = width;
        const fadeOutEndY = drawBottom; // End at bottom (min gain)
        
        // Draw smooth curve from gain level to bottom
        const segments = Math.max(10, Math.floor((fadeOutEndX - fadeOutStartX) / 2));
        for (let i = 1; i <= segments; i++) {
          const t = i / segments;
          const easedT = easeInOutCubic(t);
          const x = fadeOutStartX + (fadeOutEndX - fadeOutStartX) * t;
          const y = gainY + (fadeOutEndY - gainY) * easedT;
          ctx.lineTo(x, y);
        }
      }
    }
    
    ctx.stroke();
    
    rafRef.current = null;
    });
    
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [gain, fadeIn, fadeOut, duration, width, height, color]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: width,
        height: height,
        pointerEvents: "none",
        zIndex: 2,
      }}
    />
  );
}, areGainFadePropsEqual);
